import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Activity, Cpu, Gauge, Zap, ChevronRight, MessageCircle } from "lucide-react";
import MatrixRain from "@/components/MatrixRain";
import AgentIcon from "@/components/AgentIcon";
import AgentDetailModal from "@/components/AgentDetailModal";
import { AGENTS } from "@/data/agents";
import { useI18n } from "@/contexts/I18nContext";
import { buildWhatsAppLink } from "@/lib/contact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SAMPLE_LOG = [
  { agent: "Selena", text: "Routage de 4 demandes vers Atlas", level: "info" },
  { agent: "Atlas",  text: "Lead qualifié — Pierre L. (premium)", level: "ok" },
  { agent: "Athena", text: "Tendance détectée : panier moyen +6.4%", level: "ok" },
  { agent: "Eva",    text: "Mise à jour de 128 fiches client", level: "ok" },
  { agent: "Lexa",   text: "Contrat #A-218 sécurisé, 2 clauses ajustées", level: "warn" },
  { agent: "Sofia",  text: "Ticket #C-7741 résolu en 12s", level: "ok" },
  { agent: "Vega",   text: "12 visuels générés pour la campagne #relance", level: "ok" },
  { agent: "Lena",   text: "9 posts programmés sur 3 réseaux", level: "info" },
  { agent: "Clara",  text: "Réponse envoyée en 0.8s — satisfaction 5/5", level: "ok" },
];

export default function Cockpit() {
  const { t, lang } = useI18n();
  const [pulse, setPulse] = useState(null);
  const [active, setActive] = useState(null);
  const [logCursor, setLogCursor] = useState(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t1 = setInterval(() => setNow(new Date()), 1000);
    const t2 = setInterval(() => setLogCursor((c) => (c + 1) % SAMPLE_LOG.length), 2200);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/pulse`, { timeout: 4000 });
        if (!cancelled) setPulse(r.data);
      } catch {
        if (!cancelled) setPulse({
          uptime_pct: 99.92, active_agents: 9,
          ops_per_min: 1180 + Math.floor(Math.random() * 100),
          queue: 5 + Math.floor(Math.random() * 8),
          latency_ms: 50 + Math.floor(Math.random() * 30),
        });
      }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const visibleLog = useMemo(() => {
    const n = 6;
    const out = [];
    for (let i = 0; i < n; i++) out.push(SAMPLE_LOG[(logCursor + i) % SAMPLE_LOG.length]);
    return out;
  }, [logCursor]);

  return (
    <section data-testid="cockpit-page" className="dncm-stage min-h-[100vh] pt-24 pb-24">
      <div className="absolute inset-0 opacity-25 pointer-events-none">
        <MatrixRain density={0.5} speed={0.6} opacity={0.5} />
      </div>
      <div className="absolute inset-0 dncm-grid-bg pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden"><div className="dncm-scanline" /></div>

      <div className="dncm-content max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="dncm-pulse-dot" />
              <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/70">
                Cockpit / {lang === "fr" ? "Système actif" : "Active system"}
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
              {lang === "fr" ? "Tableau de bord" : "Dashboard"}{" "}
              <span className="dncm-gradient-text">{lang === "fr" ? "temps réel" : "real time"}</span>
            </h2>
          </div>
          <div className="flex items-center gap-3 font-mono-d text-[11px] uppercase tracking-[0.22em] text-white/55">
            <span>{now.toLocaleDateString(lang === "en" ? "en-US" : "fr-FR")}</span>
            <span className="w-px h-4 bg-white/15" />
            <span>{now.toLocaleTimeString(lang === "en" ? "en-US" : "fr-FR")}</span>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="stat-uptime" icon={<Activity className="w-4 h-4" />} label={t("twin_uptime")} value={pulse ? `${pulse.uptime_pct}%` : "—"} />
          <StatCard testid="stat-ops" icon={<Zap className="w-4 h-4" />} label={lang === "fr" ? "Opérations / min" : "Ops / min"} value={pulse ? pulse.ops_per_min.toLocaleString(lang === "en" ? "en-US" : "fr-FR") : "—"} />
          <StatCard testid="stat-queue" icon={<Cpu className="w-4 h-4" />} label={lang === "fr" ? "File d'attente" : "Queue"} value={pulse ? pulse.queue : "—"} />
          <StatCard testid="stat-latency" icon={<Gauge className="w-4 h-4" />} label={t("twin_latency")} value={pulse ? `${pulse.latency_ms}ms` : "—"} />
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
            {AGENTS.map((a) => (
              <button
                key={a.id}
                onClick={() => setActive(a)}
                data-testid={`cockpit-agent-${a.id}`}
                className="dncm-card rounded-2xl p-5 text-left group relative overflow-hidden"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span
                      className="flex items-center justify-center w-10 h-10 rounded-lg"
                      style={{
                        background: `${a.color}1A`,
                        border: `1px solid ${a.color}55`,
                        color: a.color,
                        boxShadow: `0 0 22px ${a.color}33`,
                      }}
                    >
                      <AgentIcon name={a.icon} className="w-5 h-5" />
                    </span>
                    <div>
                      <div className="font-display text-lg text-white leading-none">{a.name}</div>
                      <div className="font-mono-d text-[10px] uppercase tracking-[0.26em] text-white/55 mt-1">
                        {a.role}
                      </div>
                    </div>
                  </div>
                  <span className="dncm-pulse-dot" style={{ background: a.color, boxShadow: `0 0 12px ${a.color}` }} />
                </div>

                <div className="mt-5 grid grid-cols-12 gap-1 items-end h-12">
                  {Array.from({ length: 28 }).map((_, i) => (
                    <span
                      key={i}
                      className="dncm-bar col-span-1"
                      style={{
                        height: `${10 + ((i * 7 + a.name.length * 11) % 90)}%`,
                        opacity: 0.45 + ((i * 3) % 6) / 10,
                        background: `linear-gradient(180deg, ${a.color} 0%, ${a.color}30 100%)`,
                      }}
                    />
                  ))}
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">
                    {t("activation_label")}{" "}
                    <span className="ml-1 text-white">
                      {new Intl.NumberFormat("fr-FR").format(a.price_php)} PHP
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1 font-mono-d text-[10px] uppercase tracking-[0.22em] text-[color:var(--dncm-cyan)]">
                    {t("open")} <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="dncm-card rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div className="font-mono-d text-[11px] uppercase tracking-[0.26em] text-white/65">
                {lang === "fr" ? "Flux d'activité" : "Activity feed"}
              </div>
              <div className="flex items-center gap-2 font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45">
                <span className="dncm-pulse-dot" />
                live
              </div>
            </div>
            <ul data-testid="cockpit-log" className="mt-4 space-y-3">
              {visibleLog.map((row, i) => (
                <li key={`${row.agent}-${i}-${logCursor}`} className="flex items-start gap-3 dncm-fadein" style={{ animationDelay: `${i * 0.05}s` }}>
                  <span
                    className="mt-1 inline-block w-1.5 h-1.5 rounded-full"
                    style={{
                      background: row.level === "warn" ? "#f59e0b" : row.level === "info" ? "#a78bfa" : "#34d399",
                      boxShadow: "0 0 10px currentColor",
                    }}
                  />
                  <div className="min-w-0">
                    <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/50">{row.agent}</div>
                    <div className="text-white/80 text-sm truncate">{row.text}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 dncm-divider" />

            <a
              data-testid="cockpit-whatsapp-cta"
              href={buildWhatsAppLink("Bonjour, j'ai vu le cockpit DNCM Vision et je souhaite obtenir le mien.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-3 px-5 py-3 rounded-full font-mono-d text-[11px] uppercase tracking-[0.22em] text-[#001018] dncm-btn-glow"
              style={{ background: "linear-gradient(180deg, #6ff3ff 0%, #00cfff 60%, #00a6cf 100%)" }}
            >
              <MessageCircle className="w-4 h-4" />
              {lang === "fr" ? "Obtenir mon cockpit" : "Get my cockpit"}
            </a>
          </div>
        </div>
      </div>

      <AgentDetailModal agent={active} open={!!active} onOpenChange={(o) => !o && setActive(null)} />
    </section>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="dncm-card rounded-xl p-4 flex items-center gap-4">
      <span className="flex items-center justify-center w-9 h-9 rounded-lg text-[color:var(--dncm-cyan)]"
            style={{ background: "rgba(0,207,255,0.08)", border: "1px solid rgba(0,207,255,0.35)" }}>
        {icon}
      </span>
      <div>
        <div className="font-mono-d text-[10px] uppercase tracking-[0.24em] text-white/55">{label}</div>
        <div className="font-display text-xl text-white">{value}</div>
      </div>
    </div>
  );
}
